import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import authService from "../../services/authService";
import {
  Bot, Upload, ShoppingCart, Package, Truck, Bell, Inbox,
  TrendingUp, Image, FileText, Lock, Shield, RefreshCw,
  Trash2, Zap, Clock, AlertTriangle, CheckCircle, X,
  Play, Pause, Activity, Database, Wifi, WifiOff, Eye, ListOrdered
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/robo`;

const PainelRobo = () => {
  // Estados
  const [config, setConfig] = useState(null);
  const [marketplaces, setMarketplaces] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Carregar dados
  const loadData = useCallback(async () => {
    try {
      const [configRes, marketplacesRes, statusRes, logsRes, alertsRes] = await Promise.all([
        authService.fetchAuth(`${API}/config`),
        authService.fetchAuth(`${API}/marketplaces`),
        authService.fetchAuth(`${API}/status`),
        authService.fetchAuth(`${API}/logs?limit=50`),
        authService.fetchAuth(`${API}/alerts`)
      ]);

      if (!configRes.ok || !marketplacesRes.ok || !statusRes.ok || !logsRes.ok || !alertsRes.ok) {
        throw new Error(
          `Falha ao carregar: config=${configRes.status} marketplaces=${marketplacesRes.status} status=${statusRes.status} logs=${logsRes.status} alerts=${alertsRes.status}`
        );
      }

      setConfig(await configRes.json());
      setMarketplaces(await marketplacesRes.json());
      setStatus(await statusRes.json());
      setLogs(await logsRes.json());
      setAlerts(await alertsRes.json());
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh a cada 5 segundos
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Atualizar configuração
  const updateConfig = async (key, value) => {
    if (!config) return;
    setSaving(true);
    try {
      const newConfig = { ...config, [key]: value };
      const resp = await authService.fetchAuth(`${API}/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newConfig),
      });
      if (!resp.ok) throw new Error(`Falha ao salvar config (${resp.status})`);
      setConfig(newConfig);
      loadData(); // Recarregar logs
    } catch (error) {
      console.error("Erro ao atualizar config:", error);
    } finally {
      setSaving(false);
    }
  };

  // Atualizar marketplace
  const updateMarketplace = async (key, value) => {
    if (!marketplaces) return;
    setSaving(true);
    try {
      const newMarketplaces = { ...marketplaces, [key]: value };
      const resp = await authService.fetchAuth(`${API}/marketplaces`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newMarketplaces),
      });
      if (!resp.ok) throw new Error(`Falha ao salvar marketplace (${resp.status})`);
      setMarketplaces(newMarketplaces);
      loadData();
    } catch (error) {
      console.error("Erro ao atualizar marketplace:", error);
    } finally {
      setSaving(false);
    }
  };

  // Executar comando
  const executeCommand = async (comando) => {
    try {
      const resp = await authService.fetchAuth(`${API}/comando`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comando }),
      });
      if (!resp.ok) throw new Error(`Falha ao executar comando (${resp.status})`);
      loadData();
    } catch (error) {
      console.error("Erro ao executar comando:", error);
    }
  };

  // Resolver alerta
  const resolveAlert = async (alertId) => {
    try {
      const resp = await authService.fetchAuth(`${API}/alerts/${alertId}/resolve`, {
        method: "PUT",
      });
      if (!resp.ok) throw new Error(`Falha ao resolver alerta (${resp.status})`);
      loadData();
    } catch (error) {
      console.error("Erro ao resolver alerta:", error);
    }
  };

  // Módulos do robô
  const modules = [
    { key: "publicar_anuncios", label: "Publicar Anúncios", desc: "O robô cria anúncios sozinho nos marketplaces", icon: Upload, color: "from-blue-500 to-blue-600" },
    { key: "capturar_vendas", label: "Capturar Vendas", desc: "O robô detecta vendas automaticamente via webhooks", icon: ShoppingCart, color: "from-purple-500 to-purple-600" },
    { key: "enviar_pedido_fornecedor", label: "Enviar ao Fornecedor", desc: "Robô envia pedidos automaticamente para o fornecedor", icon: Package, color: "from-blue-400 to-blue-500", warning: true },
    { key: "rastrear_envios", label: "Rastrear Envios", desc: "Atualiza status do pedido, transportadora e entrega", icon: Truck, color: "from-green-500 to-green-600" },
    { key: "notificar_afiliado", label: "Notificar Afiliados", desc: "Robô avisa sobre vendas, envios e atualizações", icon: Bell, color: "from-yellow-500 to-yellow-600" },
    { key: "notificar_fornecedor", label: "Notificar Fornecedores", desc: "Envia confirmações de venda e instruções de postagem", icon: Inbox, color: "from-orange-500 to-orange-600" },
    { key: "ajustar_precos", label: "Preço Dinâmico", desc: "IA ajusta preços conforme concorrência", icon: TrendingUp, color: "from-red-500 to-red-600" },
    { key: "gerar_imagens_ia", label: "Imagens com IA", desc: "Cria capas profissionais automaticamente", icon: Image, color: "from-pink-500 to-pink-600" },
    { key: "gerar_descricao_ia", label: "Descrições IA", desc: "Robô cria textos otimizados para vendas", icon: FileText, color: "from-indigo-500 to-indigo-600" },
  ];

  // Módulos críticos
  const criticalModules = [
    { key: "liberar_repasses", label: "Liberação de Repasses", desc: "O robô NUNCA libera dinheiro sozinho. Apenas você.", icon: Lock },
    { key: "liberar_pix_fornecedor", label: "Pagamentos PIX", desc: "Segurança máxima. Robô não tem permissão de pagamento.", icon: Shield },
  ];

  // Marketplaces
  const marketplaceList = [
    { key: "mercado_livre", name: "Mercado Livre", logo: "🟡" },
    { key: "shopee", name: "Shopee", logo: "🟠" },
    { key: "amazon", name: "Amazon", logo: "📦" },
    { key: "tiktok", name: "TikTok Shop", logo: "🎵" },
    { key: "havan", name: "Havan", logo: "🔵" },
    { key: "magalu", name: "Magalu", logo: "💙" },
    { key: "americanas", name: "Americanas", logo: "🔴" },
    { key: "casas_bahia", name: "Casas Bahia", logo: "🟡" },
    { key: "shein", name: "Shein", logo: "⚫" },
    { key: "aliexpress", name: "AliExpress", logo: "🔶" },
    { key: "tray", name: "Tray", logo: "🟢" },
    { key: "shopify", name: "Shopify", logo: "💚" },
    { key: "woocommerce", name: "WooCommerce", logo: "🟣" },
    { key: "nuvemshop", name: "Nuvemshop", logo: "☁️" },
  ];

  // Formatador de data
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Cor do log
  const getLogColor = (tipo) => {
    switch (tipo) {
      case "success": return "text-green-400";
      case "error": return "text-red-400";
      case "warning": return "text-yellow-400";
      default: return "text-blue-400";
    }
  };

  // Cor do alerta
  const getAlertColor = (prioridade) => {
    switch (prioridade) {
      case "high": return "bg-red-500/20 border-red-500/50 text-red-400";
      case "medium": return "bg-yellow-500/20 border-yellow-500/50 text-yellow-400";
      default: return "bg-green-500/20 border-green-500/50 text-green-400";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-950">
        <div className="text-center">
          <Bot className="w-16 h-16 mx-auto mb-4 text-orange-500 animate-pulse" />
          <p className="text-gray-400">Carregando Painel do Robô...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-950">
      <div className="flex-1">
        <div className="p-4 lg:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Bot className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">🤖 Robô ClickHub</h1>
                <p className="text-gray-400 text-sm">Controle Geral - Gerencie todos os módulos em tempo real</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {status?.status === "online" ? (
                <span className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-full text-sm font-semibold">
                  <Wifi className="w-4 h-4" />
                  Online
                </span>
              ) : status?.status === "paused" ? (
                <span className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-semibold">
                  <Pause className="w-4 h-4" />
                  Pausado
                </span>
              ) : (
                <span className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-full text-sm font-semibold">
                  <WifiOff className="w-4 h-4" />
                  Offline
                </span>
              )}
              {saving && (
                <span className="text-orange-400 text-sm animate-pulse">Salvando...</span>
              )}
            </div>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 border-none">
              <div className="text-center">
                <Activity className="w-6 h-6 mx-auto mb-1 text-white/80" />
                <p className="text-2xl font-bold text-white">{status?.execucoes_minuto || 0}</p>
                <p className="text-xs text-white/80">Exec/min</p>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 p-3 border-none">
              <div className="text-center">
                <ShoppingCart className="w-6 h-6 mx-auto mb-1 text-white/80" />
                <p className="text-2xl font-bold text-white">{status?.vendas_hoje || 0}</p>
                <p className="text-xs text-white/80">Vendas Hoje</p>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 border-none">
              <div className="text-center">
                <Upload className="w-6 h-6 mx-auto mb-1 text-white/80" />
                <p className="text-2xl font-bold text-white">{status?.anuncios_publicados_hoje || 0}</p>
                <p className="text-xs text-white/80">Anúncios Hoje</p>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-red-500 to-red-600 p-3 border-none">
              <div className="text-center">
                <AlertTriangle className="w-6 h-6 mx-auto mb-1 text-white/80" />
                <p className="text-2xl font-bold text-white">{status?.taxa_falhas || 0}%</p>
                <p className="text-xs text-white/80">Taxa Falhas</p>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 border-none">
              <div className="text-center">
                <Database className="w-6 h-6 mx-auto mb-1 text-white/80" />
                <p className="text-2xl font-bold text-white">{status?.marketplaces_ativos || 0}</p>
                <p className="text-xs text-white/80">Marketplaces</p>
              </div>
            </Card>
            <Card className="bg-gradient-to-br from-pink-500 to-pink-600 p-3 border-none">
              <div className="text-center">
                <Zap className="w-6 h-6 mx-auto mb-1 text-white/80" />
                <p className="text-2xl font-bold text-white">{status?.modulos_ativos || 0}</p>
                <p className="text-xs text-white/80">Módulos Ativos</p>
              </div>
            </Card>
          </div>

          {/* Módulos do Robô */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-400" />
              Módulos do Robô (ON/OFF)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {modules.map((module) => {
                const Icon = module.icon;
                const isOn = config?.[module.key] || false;
                return (
                  <Card 
                    key={module.key}
                    className={`bg-gray-800/50 border-gray-700 p-4 transition-all ${isOn ? 'border-green-500/50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${module.color} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold text-sm">{module.label}</h3>
                          <p className="text-gray-400 text-xs">{module.desc}</p>
                        </div>
                      </div>
                      <Switch
                        checked={isOn}
                        onCheckedChange={(value) => updateConfig(module.key, value)}
                        className="data-[state=checked]:bg-green-500"
                      />
                    </div>
                    {module.warning && (
                      <p className="text-yellow-400 text-xs mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Recomendado deixar OFF
                      </p>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Módulos Críticos */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-400" />
              Funções Críticas (Somente Admin)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {criticalModules.map((module) => {
                const Icon = module.icon;
                return (
                  <Card 
                    key={module.key}
                    className="bg-red-500/10 border-red-500/30 p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                          <h3 className="text-red-400 font-semibold text-sm">{module.label}</h3>
                          <p className="text-red-300/70 text-xs">{module.desc}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-red-400" />
                        <span className="text-red-400 text-xs font-semibold">BLOQUEADO</span>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Marketplaces */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-400" />
              Marketplaces Ativos
            </h2>
            <Card className="bg-gray-800/50 border-gray-700 p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {marketplaceList.map((mp) => {
                  const isOn = marketplaces?.[mp.key] || false;
                  return (
                    <div 
                      key={mp.key}
                      className={`flex items-center justify-between p-2 rounded-lg transition-all ${isOn ? 'bg-green-500/10 border border-green-500/30' : 'bg-gray-700/30'}`}
                    >
                      <span className="text-sm flex items-center gap-1.5">
                        <span>{mp.logo}</span>
                        <span className={isOn ? 'text-white' : 'text-gray-400'}>{mp.name}</span>
                      </span>
                      <Switch
                        checked={isOn}
                        onCheckedChange={(value) => updateMarketplace(mp.key, value)}
                        className="data-[state=checked]:bg-green-500 scale-75"
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Ações Rápidas */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Play className="w-5 h-5 text-green-400" />
              Ações Rápidas
            </h2>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => executeCommand('reiniciar')}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reiniciar Robô
              </Button>
              <Button
                onClick={() => executeCommand(status?.status === 'paused' ? 'retomar' : 'pausar')}
                className={status?.status === 'paused' ? 'bg-green-500 hover:bg-green-600' : 'bg-yellow-500 hover:bg-yellow-600'}
              >
                {status?.status === 'paused' ? (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Retomar Robô
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Pausar Robô
                  </>
                )}
              </Button>
              <Button
                onClick={() => executeCommand('sincronizar')}
                className="bg-purple-500 hover:bg-purple-600"
              >
                <Zap className="w-4 h-4 mr-2" />
                Forçar Sincronização
              </Button>
              <Button
                onClick={() => executeCommand('limpar_logs')}
                variant="outline"
                className="border-gray-700 hover:bg-gray-800"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar Logs
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Terminal de Logs */}
            <div>
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Terminal do Robô (Ao Vivo)
              </h2>
              <Card className="bg-gray-900 border-gray-700 p-3 h-80">
                <ScrollArea className="h-full">
                  <div className="font-mono text-xs space-y-1">
                    {logs.map((log, index) => (
                      <div key={log.id || index} className="flex gap-2">
                        <span className="text-gray-500">[{formatDate(log.data)}]</span>
                        <span className={getLogColor(log.tipo)}>{log.mensagem}</span>
                      </div>
                    ))}
                    {logs.length === 0 && (
                      <p className="text-gray-500 text-center py-8">Nenhum log ainda...</p>
                    )}
                  </div>
                </ScrollArea>
              </Card>
            </div>

            {/* Alertas */}
            <div>
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Alertas do Robô
              </h2>
              <Card className="bg-gray-800/50 border-gray-700 p-3 h-80">
                <ScrollArea className="h-full">
                  <div className="space-y-2">
                    {alerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`p-3 rounded-lg border ${getAlertColor(alert.prioridade)} flex items-center justify-between`}
                      >
                        <div className="flex items-center gap-2">
                          {alert.prioridade === "high" && <AlertTriangle className="w-4 h-4" />}
                          {alert.prioridade === "medium" && <AlertTriangle className="w-4 h-4" />}
                          {alert.prioridade === "low" && <CheckCircle className="w-4 h-4" />}
                          <span className="text-sm">{alert.mensagem}</span>
                        </div>
                        {alert.status === "pending" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => resolveAlert(alert.id)}
                          >
                            Resolver
                          </Button>
                        )}
                        {alert.status === "resolved" && (
                          <span className="text-xs text-green-400">✓ Resolvido</span>
                        )}
                      </div>
                    ))}
                    {alerts.length === 0 && (
                      <p className="text-gray-500 text-center py-8">Nenhum alerta</p>
                    )}
                  </div>
                </ScrollArea>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PainelRobo;
